import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Calendar, ExternalLink, Award } from 'lucide-react';
import styles from './DirectorioUniversidades.module.css';
import becasEstatales from '../images/becas-estatales.png';
import becasMunicipales from '../images/becas-municipales.png'; 
import becasPrivadas from '../images/becas-privadas.png';
import becasFederales from '../images/becas-federales.jpg';
import otrasBecas from '../images/otras-becas.png';
import {useAuth} from '../../../Auth/AuthContext'; 

const DirectorioBecas = () => {
    const navigate = useNavigate();
    const [selectedTipoBeca, setSelectedTipoBeca] = useState(null);
    const [selectedUniversidad, setSelectedUniversidad] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isUniversidadModalOpen, setIsUniversidadModalOpen] = useState(false);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [becas, setBecas] = useState([]);
    const [becasPorTipo, setBecasPorTipo] = useState({});
    const [universidadesPorTipo, setUniversidadesPorTipo] = useState({});
    const {getToken} = useAuth(); 
    const token = getToken(); 

    const tiposBecas = {
        1: 'Estatales',
        2: 'Federales',
        3: 'Municipales',
        4: 'Escolares',
        5: 'Otras Becas Disponibles'
    };

    const imagenesURL = {
        1: becasEstatales,
        2: becasFederales,
        3: becasMunicipales, 
        4: becasPrivadas,
        5: otrasBecas
    };

    useEffect(() => {
        const fetchBecas = async () => {
            try {
                const response = await fetch(`${process.env.REACT_APP_API_URL}/universidades/becas`, { 
                    headers: { 
                        'Authorization':  `Bearer ${token}`
                    }
                });
                if (!response.ok) {
                    throw new Error('Error al obtener las becas');
                }
                const data = await response.json();
                
                const becasFiltradas = data.filter(beca => beca.tipo !== 6);
                setBecas(becasFiltradas);
                
                // Agrupar becas por tipo
                const becasAgrupadas = becasFiltradas.reduce((acc, beca) => {
                    const tipo = beca.tipo;
                    if (!acc[tipo]) {
                        acc[tipo] = [];
                    }
                    acc[tipo].push(beca);
                    return acc;
                }, {});
                
                setBecasPorTipo(becasAgrupadas);

                // Agrupar universidades por tipo para becas escolares (tipo 4)
                const universidadesEscolares = becasFiltradas
                    .filter(beca => beca.tipo === 4 && beca.universidad && beca.universidad_tipo !== 8)
                    .reduce((acc, beca) => {
                        const univId = beca.universidad;
                        if (!acc[univId]) {
                            acc[univId] = {
                                id: univId,
                                nombre: beca.universidad_nombre,
                                logo: beca.universidad_logo,
                                pagina: beca.universidad_pagina,
                                tipo: beca.universidad_tipo,
                                becas: []
                            };
                        }
                        acc[univId].becas.push(beca);
                        return acc;
                    }, {});

                setUniversidadesPorTipo(universidadesEscolares);
                setLoading(false);
            } catch (err) {
                setError(err.message);
                setLoading(false);
            }
        };

        fetchBecas();
    }, []);

    const navigateTo = (route) => {
        navigate(route);
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'No especificada';
        const date = new Date(dateString);
        return date.toLocaleDateString('es-MX', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };

    const isExpired = (fechaFinal) => {
        if (!fechaFinal) return false;
        const today = new Date();
        const endDate = new Date(fechaFinal);
        return endDate < today;
    };

    const getDaysRemaining = (fechaFinal) => {
        if (!fechaFinal) return null;
        const today = new Date();
        const endDate = new Date(fechaFinal);
        const diffTime = endDate - today;
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        return diffDays;
    };

    if (loading) {
        return (
            <div className={styles.loadingContainer}>
                <div className={styles.loadingContent}>
                    <div className={styles.loadingSpinner}></div>
                    <p className={styles.loadingText}>Cargando becas...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className={styles.loadingContainer}>
                <div className={styles.loadingContent}>
                    <p className={styles.loadingText}>Error: {error}</p>
                </div>
            </div>
        );
    }

    const handleCardClick = (tipo) => {
        setSelectedTipoBeca(tipo);
        setIsModalOpen(true);
    };

    const handleUniversidadClick = (universidad) => {
        setSelectedUniversidad(universidad);
        setIsUniversidadModalOpen(true);
    };

    const closeModal = () => {
        setIsModalOpen(false);
        setSelectedTipoBeca(null);
    };

    const closeUniversidadModal = () => {
        setIsUniversidadModalOpen(false);
        setSelectedUniversidad(null);
    };

    const MunicipiosInf = ({ name, url, description }) => (
        <p className={styles.infoText}>
            <span className={styles.municipalityName}>{name}</span> - {description}{' '}
            <a 
                href={url} 
                target="_blank" 
                rel="noopener noreferrer"
                className={styles.externalLink}
                aria-label={`Ver programas de ${name}`}
            >
                Ver
            </a>
        </p>
    );

    const TipoBecaCard = ({ tipo }) => {
        return (
            <div
                className={styles.universityCard}
                onClick={() => handleCardClick(tipo)}
                tabIndex={0}
                onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleCardClick(tipo);
                    }
                }}
            >
                <div className={styles.universityCardLogo}>
                    <img 
                        src={imagenesURL[tipo]} 
                        alt={`Becas ${tiposBecas[tipo]}`}
                        className={styles.universityCardLogoImg}
                        onError={(e) => {
                            e.target.style.display = 'none';
                            const fallback = document.createElement('div');
                            fallback.className = styles.fallbackImage;
                            fallback.innerHTML = '📚';
                            e.target.parentElement.appendChild(fallback);
                        }}
                    />
                </div>
                <h3 className={styles.universityCardTitle}>
                    {tiposBecas[tipo] === 'Otras Becas Disponibles' ? (
                        <div>{tiposBecas[tipo]}</div>
                    ) : (
                        <div>Becas {tiposBecas[tipo]}</div>
                    )}
                </h3>
            </div>
        );
    };

    // Componente para mostrar las universidades
    const UniversidadCard = ({ universidad }) => {
        const totalBecas = universidad.becas.length;
        const becasActivas = universidad.becas.filter(beca => !isExpired(beca.fecha_final)).length;

        return (
            <div
                className={styles.universityCard}
                onClick={() => handleUniversidadClick(universidad)}
                tabIndex={0}
                onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleUniversidadClick(universidad);
                    }
                }}
            >
                <div className={styles.universityCardLogo}>
                    <img 
                        src={`${process.env.REACT_APP_API_URL}/universidades/logos-universidades/${universidad.logo}`}
                        alt={`Logo de ${universidad.nombre}`}
                        className={styles.universityCardLogoImg}
                        onError={(e) => {
                            console.error(`Error cargando logo: ${universidad.logo}`);
                            console.error(`URL intentada: ${e.target.src}`);
                            e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjEwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSIjZjNmNGY2Ii8+PHRleHQgeD0iNTAlIiB5PSI1MCUiIGZvbnQtZmFtaWx5PSJBcmlhbCwgc2Fucy1zZXJpZiIgZm9udC1zaXplPSIxMiIgZmlsbD0iIzZiNzI4MCIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZHk9Ii4zZW0iPkxvZ28gbm8gZGlzcG9uaWJsZTwvdGV4dD48L3N2Zz4=';
                        }}
                    />
                </div>
                <h3 className={styles.universityCardTitle}>
                    {universidad.nombre}
                </h3>
                <p className={styles.becasCount}>
                    {becasActivas > 0 ? (
                        <span>{becasActivas} beca{becasActivas !== 1 ? 's' : ''} activa{becasActivas !== 1 ? 's' : ''}</span>
                    ) : (
                        <span style={{color: '#9ca3af'}}>Sin becas activas</span>
                    )}
                </p>
            </div>
        );
    };

    // Modal para universidades específicas
    const UniversidadModal = ({ isOpen, universidad, onClose }) => {
        if (!isOpen || !universidad) return null;

        return (
            <div className={styles.modalOverlay} onClick={onClose}>
                <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
                    <div className={styles.modalHeader}>
                        <button
                            className={styles.modalClose}
                            onClick={onClose}
                            type="button"
                            aria-label="Cerrar modal"
                        >
                            ×
                        </button>
                    </div>

                    <div className={styles.modalBody}>
                        <div className={styles.modalHeaderContent}>
                            <div className={styles.modalHeaderImageContainer}>
                                <img 
                                    src={`${process.env.REACT_APP_API_URL}/universidades/logos-universidades/${universidad.logo}`}
                                    alt={`Logo de ${universidad.nombre}`}
                                    className={styles.modalHeaderImage}
                                    onError={(e) => {
                                        console.error(`Error cargando logo: ${universidad.logo}`);
                                        console.error(`URL intentada: ${e.target.src}`);
                                        e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjEwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSIjZjNmNGY2Ii8+PHRleHQgeD0iNTAlIiB5PSI1MCUiIGZvbnQtZmFtaWx5PSJBcmlhbCwgc2Fucy1zZXJpZiIgZm9udC1zaXplPSIxMiIgZmlsbD0iIzZiNzI4MCIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZHk9Ii4zZW0iPkxvZ28gbm8gZGlzcG9uaWJsZTwvdGV4dD48L3N2Zz4=';
                                    }}
                                />
                            </div>
                            <h2 className={styles.modalTitle}>
                                {universidad.nombre}
                            </h2>
                        </div>
                        
                        {universidad.becas.length === 0 ? (
                            <div className={styles.modalDetail}>
                                <p>No hay becas disponibles en esta universidad.</p>
                            </div>
                        ) : (
                            <div className={styles.plantelesSection}>
                                <h3 className={styles.sectionTitle}>
                                    {universidad.becas.length} beca{universidad.becas.length !== 1 ? 's' : ''} disponible{universidad.becas.length !== 1 ? 's' : ''}
                                </h3>
                                
                                {universidad.becas.map((beca) => {
                                    const expired = isExpired(beca.fecha_final);
                                    const daysRemaining = getDaysRemaining(beca.fecha_final);
                                    
                                    return (
                                        <div key={beca.id} className={styles.plantelSection}>
                                            <div className={`${styles.plantelContent} ${expired ? styles.becaExpirada : ''}`}>
                                                <div className={styles.becaTitleContainer}>
                                                    <Award size={20} color={expired ? '#9ca3af' : '#3b82f6'} />
                                                    <h4 className={`${styles.becaNombre} ${expired ? styles.becaNombreExpirada : ''}`}>
                                                        {beca.nombre}
                                                        {expired && <span className={styles.expiradaTag}>(EXPIRADA)</span>}
                                                    </h4>
                                                </div>
                                                
                                                {beca.info && (
                                                    <div className={styles.modalDetail}>
                                                        <strong>Información:</strong>
                                                        <div className={styles.becaInfo}>
                                                            {beca.info}
                                                        </div>
                                                    </div>
                                                )}
                                                
                                                <div className={styles.becaDetallesContainer}>
                                                    <div className={styles.fechasContainer}>
                                                        {beca.fecha_inicio && (
                                                            <div className={styles.modalDetail}>
                                                                <div className={styles.fechaHeader}>
                                                                    <Calendar size={16} />
                                                                    <strong>Inicio:</strong>
                                                                </div>
                                                                <div className={styles.fechaTexto}>
                                                                    {formatDate(beca.fecha_inicio)}
                                                                </div>
                                                            </div>
                                                        )}
                                                        
                                                        {beca.fecha_final && (
                                                            <div className={styles.modalDetail}>
                                                                <div className={styles.fechaHeader}>
                                                                    <Calendar size={16} />
                                                                    <strong>Fin:</strong>
                                                                </div>
                                                                <div className={`${styles.fechaTexto} ${expired ? styles.fechaExpirada : (daysRemaining <= 30 ? styles.fechaProxima : styles.fechaActiva)}`}>
                                                                    {formatDate(beca.fecha_final)}
                                                                </div>
                                                            </div>
                                                        )}
                                                    </div>
                                                    
                                                    {beca.link && (
                                                        <div className={styles.enlaceContainer}>
                                                            <a
                                                                href={beca.link}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                className={styles.becaLinkButton}
                                                            >
                                                                Ver convocatoria
                                                                <ExternalLink size={14} />
                                                            </a>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                        
                        {universidad.pagina && (
                            <div className={styles.enlaceContainer} style={{marginTop: '20px'}}>
                                <a
                                    href={universidad.pagina}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className={styles.becaLinkButton}
                                    style={{backgroundColor: '#10b981'}}
                                >
                                    Visitar página oficial
                                    <ExternalLink size={14} />
                                </a>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        );
    };

    const Modal = ({ isOpen, tipoBeca, onClose }) => {
        if (!isOpen || !tipoBeca) return null;

        // Para becas escolares (tipo 4), mostrar universidades
        if (tipoBeca === 4) {
            const universidadesDisponibles = Object.values(universidadesPorTipo);
            
            return (
                <div className={styles.modalOverlay} onClick={onClose}>
                    <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
                        <div className={styles.modalHeader}>
                            <button
                                className={styles.modalClose}
                                onClick={onClose}
                                type="button"
                                aria-label="Cerrar modal"
                            >
                                ×
                            </button>
                        </div>

                        <div className={styles.modalBody}>
                            <div className={styles.modalHeaderContent}>
                                <div className={styles.modalHeaderImageContainer}>
                                    <img 
                                        src={imagenesURL[tipoBeca]} 
                                        alt={`Becas ${tiposBecas[tipoBeca]}`}
                                        className={styles.modalHeaderImage}
                                        onError={(e) => {
                                            e.target.style.display = 'none';
                                            const fallback = document.createElement('div');
                                            fallback.className = styles.fallbackImage;
                                            fallback.innerHTML = '📚';
                                            e.target.parentElement.appendChild(fallback);
                                        }}
                                    />
                                </div>
                                <h2 className={styles.modalTitle}>
                                    Becas {tiposBecas[tipoBeca]}
                                </h2>
                            </div>
                            
                            {universidadesDisponibles.length === 0 ? (
                                <div className={styles.modalDetail}>
                                    <p>No hay universidades con becas disponibles en esta categoría.</p>
                                </div>
                            ) : (
                                <div className={styles.plantelesSection}>
                                    <h3 className={styles.sectionTitle}>
                                        {universidadesDisponibles.length} universidad{universidadesDisponibles.length !== 1 ? 'es' : ''} disponible{universidadesDisponibles.length !== 1 ? 's' : ''}
                                    </h3>
                                    
                                    <div className={styles.grid}>
                                        {universidadesDisponibles.map((universidad) => (
                                            <UniversidadCard 
                                                key={universidad.id} 
                                                universidad={universidad} 
                                            />
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            );
        }

        // Para otros tipos de becas, mantener el comportamiento original
        const becasDelTipo = becasPorTipo[tipoBeca] || [];
        const nombreTipo = tiposBecas[tipoBeca];

        return (
            <div className={styles.modalOverlay} onClick={onClose}>
                <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
                    <div className={styles.modalHeader}>
                        <button
                            className={styles.modalClose}
                            onClick={onClose}
                            type="button"
                            aria-label="Cerrar modal"
                        >
                            ×
                        </button>
                    </div>

                    <div className={styles.modalBody}>
                        <div className={styles.modalHeaderContent}>
                            <div className={styles.modalHeaderImageContainer}>
                                <img 
                                    src={imagenesURL[tipoBeca]} 
                                    alt={`Becas ${nombreTipo}`}
                                    className={styles.modalHeaderImage}
                                    onError={(e) => {
                                        e.target.style.display = 'none';
                                        const fallback = document.createElement('div');
                                        fallback.className = styles.fallbackImage;
                                        fallback.innerHTML = '📚';
                                        e.target.parentElement.appendChild(fallback);
                                    }}
                                />
                            </div>
                            <h2 className={styles.modalTitle}>
                                {nombreTipo === 'Otras Becas Disponibles' ? (
                                    <div>{nombreTipo}</div>
                                ) : (
                                    <div>Becas {nombreTipo}</div>
                                )}
                            </h2>
                        </div>
                        
                        {becasDelTipo.length === 0 ? (
                            <div className={styles.modalDetail}>
                                <p>No hay becas disponibles en esta categoría.</p>
                            </div>
                        ) : (
                            <div className={styles.plantelesSection}>
                                <h3 className={styles.sectionTitle}>
                                    {becasDelTipo.length} beca{becasDelTipo.length !== 1 ? 's' : ''} disponible{becasDelTipo.length !== 1 ? 's' : ''}
                                </h3>
                                
                                {becasDelTipo.map((beca) => {
                                    const expired = isExpired(beca.fecha_final);
                                    const daysRemaining = getDaysRemaining(beca.fecha_final);
                                    
                                    return (
                                        <div key={beca.id} className={styles.plantelSection}>
                                            <div className={`${styles.plantelContent} ${expired ? styles.becaExpirada : ''}`}>
                                                <div className={styles.becaTitleContainer}>
                                                    <Award size={20} color={expired ? '#9ca3af' : '#3b82f6'} />
                                                    <h4 className={`${styles.becaNombre} ${expired ? styles.becaNombreExpirada : ''}`}>
                                                        {beca.nombre}
                                                        {expired && <span className={styles.expiradaTag}>(EXPIRADA)</span>}
                                                    </h4>
                                                </div>
                                                
                                                {beca.info && (
                                                    <div className={styles.modalDetail}>
                                                        <strong>Información:</strong>
                                                        <div className={styles.becaInfo}>
                                                            {beca.info}
                                                        </div>
                                                    </div>
                                                )}
                                                
                                                <div className={styles.becaDetallesContainer}>
                                                    <div className={styles.fechasContainer}>
                                                        {beca.fecha_inicio && (
                                                            <div className={styles.modalDetail}>
                                                                <div className={styles.fechaHeader}>
                                                                    <Calendar size={16} />
                                                                    <strong>Inicio:</strong>
                                                                </div>
                                                                <div className={styles.fechaTexto}>
                                                                    {formatDate(beca.fecha_inicio)}
                                                                </div>
                                                            </div>
                                                        )}
                                                        
                                                        {beca.fecha_final && (
                                                            <div className={styles.modalDetail}>
                                                                <div className={styles.fechaHeader}>
                                                                    <Calendar size={16} />
                                                                    <strong>Fin:</strong>
                                                                </div>
                                                                <div className={`${styles.fechaTexto} ${expired ? styles.fechaExpirada : (daysRemaining <= 30 ? styles.fechaProxima : styles.fechaActiva)}`}>
                                                                    {formatDate(beca.fecha_final)}
                                                                </div>
                                                            </div>
                                                        )}
                                                    </div>
                                                    
                                                    {beca.link && (
                                                        <div className={styles.enlaceContainer}>
                                                            <a
                                                                href={beca.link}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                className={styles.becaLinkButton}
                                                            >
                                                                Ver convocatoria
                                                                <ExternalLink size={14} />
                                                            </a>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                        {tipoBeca === 3 && (
                            <div>
                                <h2>Más información</h2>
                                <div className={styles.linksContainer}>
                                    <MunicipiosInf 
                                        name="Municipio de Amealco de Bonfil"
                                        url="https://amealco.gob.mx/portal/"
                                        description="Consulta la página oficial"
                                    />
                                    <MunicipiosInf 
                                        name="Municipio de Arroyo Seco"
                                        url="https://arroyoseco.gob.mx/"
                                        description="Consulta la página oficial"
                                    />
                                    <MunicipiosInf 
                                        name="Municipio de Cadereyta de Montes"
                                        url="https://cadereytademontes.gob.mx/"
                                        description="Consulta la página oficial"
                                    />
                                    <MunicipiosInf 
                                        name="Municipio de Colón"
                                        url="https://colon.gob.mx/inicio/"
                                        description="Consulta la página oficial"
                                    />
                                    <MunicipiosInf 
                                        name="Municipio de Corregidora"
                                        url="https://corregidora.gob.mx/portal/"
                                        description="Consulta la página oficial"
                                    />
                                    <MunicipiosInf 
                                        name="Municipio de Ezequiel Montes"
                                        url="https://ezequielmontes.gob.mx/index.php/programas-y-apoyos/programas-municipales"
                                        description="Consulta todos los programas disponibles"
                                    />
                                    <MunicipiosInf 
                                        name="Municipio de Huimilpan"
                                        url="https://huimilpan.gob.mx/"
                                        description="Consulta la página oficial"
                                    />
                                    <MunicipiosInf 
                                        name="Municipio de Jalpan de Serra"
                                        url="https://jalpan.gob.mx/"
                                        description="Consulta la página oficial"
                                    />
                                    <MunicipiosInf 
                                        name="Municipio de Landa de Matamoros"
                                        url="https://landadematamorosqro.gob.mx/"
                                        description="Consulta la página oficial"
                                    />
                                    <MunicipiosInf 
                                        name="Municipio del Marqués"
                                        url="https://elmarques.gob.mx/"
                                        description="Consulta la página oficial"
                                    />
                                    <MunicipiosInf 
                                        name="Municipio de Pedro Escobedo"
                                        url="https://pedroescobedo.gob.mx/"
                                        description="Consulta la página oficial"
                                    />
                                    <MunicipiosInf 
                                        name="Municipio de Peñamiller"
                                        url="https://www.penamillerqro.gob.mx/"
                                        description="Consulta la página oficial"
                                    />
                                    <MunicipiosInf 
                                        name="Municipio de Pinal de Amoles"
                                        url="https://pinaldeamoles.gob.mx/"
                                        description="Consulta la página oficial"
                                    />
                                    <MunicipiosInf 
                                        name="Municipio de Querétaro"
                                        url="https://municipiodequeretaro.gob.mx/programas/"
                                        description="Consulta todos los programas disponibles"
                                    />
                                    <MunicipiosInf 
                                        name="Municipio de San Joaquín"
                                        url="https://www.sanjoaquin.gob.mx/"
                                        description="Consulta la página oficial"
                                    />
                                    <MunicipiosInf 
                                        name="Municipio de San Juan del Río"
                                        url="https://sanjuandelrio.gob.mx/"
                                        description="Consulta la página oficial"
                                    />
                                    <MunicipiosInf 
                                        name="Municipio de Tequisquiapan"
                                        url="http://tequisquiapan.gob.mx/"
                                        description="Consulta la página oficial"
                                    />
                                    <MunicipiosInf 
                                        name="Municipio de Tolimán"
                                        url="https://www.toliman.gob.mx/"
                                        description="Consulta la página oficial"
                                    />
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        );
    };

    const ordenTipos = [1, 2, 4, 3, 5];

    return (
        <div className={styles.container}>
            <div className={styles.wrapper}>
                <div className={styles.content}>
                    <div className={styles.grid}>
                        {ordenTipos.map((tipo) => (
                            <TipoBecaCard 
                                key={tipo} 
                                tipo={tipo} 
                                becasDelTipo={becasPorTipo[tipo]} 
                            />
                        ))}
                    </div>
                </div>
            </div>

            <Modal
                isOpen={isModalOpen}
                tipoBeca={selectedTipoBeca}
                onClose={closeModal}
            />

            <UniversidadModal
                isOpen={isUniversidadModalOpen}
                universidad={selectedUniversidad}
                onClose={closeUniversidadModal}
            />
        </div>
    );
};

export default DirectorioBecas;